// Abstrae GPS y notificaciones: usa los plugins nativos de Capacitor cuando
// la app corre empaquetada (Android/iOS), y las Web APIs del navegador
// cuando corre como sitio web normal. Así el mismo código funciona en
// `yarn start` y en el APK/IPA generado con Capacitor.

import { Capacitor } from "@capacitor/core";

const isNative = () => Capacitor.isNativePlatform();

let _notifId = 1;

// ---------- Geolocalización ----------

export async function getCurrentPosition() {
  if (isNative()) {
    const { Geolocation } = await import("@capacitor/geolocation");
    const perm = await Geolocation.checkPermissions();
    if (perm.location !== "granted") {
      const req = await Geolocation.requestPermissions();
      if (req.location !== "granted") {
        throw new Error("Permiso de ubicación denegado");
      }
    }
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 8000,
    });
    return {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    };
  }

  // Web fallback
  if (!navigator.geolocation) {
    throw new Error("GPS no disponible en este navegador");
  }
  const pos = await new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000 })
  );
  return {
    latitude: pos.coords.latitude,
    longitude: pos.coords.longitude,
    accuracy: pos.coords.accuracy,
  };
}

// ---------- Notificaciones locales ----------

export async function getNotificationPermission() {
  if (isNative()) {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perm = await LocalNotifications.checkPermissions();
    return perm.display; // "granted" | "denied" | "prompt"
  }
  return typeof Notification !== "undefined" ? Notification.permission : "default";
}

export async function requestNotificationPermission() {
  if (isNative()) {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perm = await LocalNotifications.requestPermissions();
    return perm.display;
  }
  if (typeof Notification === "undefined") return "denied";
  return Notification.requestPermission();
}

// Tipo de acción registrado una sola vez para que las notificaciones nativas
// (Android/iOS empaquetados) puedan mostrar un botón real — no tiene efecto
// en la PWA web, que usa las `actions` del Service Worker directamente.
let _sosActionTypeRegistered = false;
export async function ensureSosActionType() {
  if (!isNative() || _sosActionTypeRegistered) return;
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  await LocalNotifications.registerActionTypes({
    types: [{ id: "SOS_ACTIVE", actions: [{ id: "view", title: "Ver estado" }] }],
  });
  _sosActionTypeRegistered = true;
}

/**
 * Muestra una notificación local inmediata.
 * @param {string} title
 * @param {string} body
 * @param {{ tag?: string, url?: string, actions?: {action: string, title: string}[] }} opts
 */
export async function showLocalNotification(title, body, opts = {}) {
  if (isNative()) {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.schedule({
      notifications: [
        {
          id: _notifId++,
          title,
          body,
          extra: { url: opts.url || "/sos" },
          // Solo aplica el tipo de acción "botón" si de verdad se pidieron
          // acciones (la mayoría de las notificaciones locales no las necesitan).
          ...(opts.actions?.length ? { actionTypeId: "SOS_ACTIVE" } : {}),
        },
      ],
    });
    return;
  }

  // Web fallback: notificación del navegador vía Service Worker (persiste
  // aunque la pestaña esté cerrada) si está disponible, si no, Notification directa.
  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, {
        body,
        tag: opts.tag,
        requireInteraction: true,
        icon: "/favicon.ico",
        data: { url: opts.url || "/sos" },
        actions: opts.actions || [],
      });
      return;
    } catch {
      // sigue al fallback de abajo
    }
  }
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    new Notification(title, { body, tag: opts.tag, requireInteraction: true });
  }
}

export function registerServiceWorkerIfWeb() {
  if (isNative()) return; // no aplica dentro de la app nativa
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  }
}

// ---------- Push notifications reales (FCM) ----------

export function getPlatform() {
  return Capacitor.getPlatform(); // "android" | "ios" | "web"
}

/**
 * Pide permiso y devuelve el token real de push para este dispositivo:
 * - App nativa (Android/iOS empaquetada con Capacitor): usa FCM nativo.
 * - Navegador: usa Firebase Web Push (ver lib/firebase.js).
 * Devuelve null si el usuario negó el permiso o la plataforma no lo soporta.
 */
export async function getPushToken() {
  if (isNative()) {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const current = await PushNotifications.checkPermissions();
    let status = current.receive;
    if (status !== "granted") {
      const req = await PushNotifications.requestPermissions();
      status = req.receive;
    }
    if (status !== "granted") return null;

    return new Promise((resolve) => {
      let done = false;
      const finish = (value) => {
        if (!done) { done = true; resolve(value); }
      };
      PushNotifications.addListener("registration", (token) => finish(token.value));
      PushNotifications.addListener("registrationError", () => finish(null));
      PushNotifications.register();
      setTimeout(() => finish(null), 8000); // por si el sistema nunca responde
    });
  }

  const { getFcmToken } = await import("./firebase");
  return getFcmToken();
}

/**
 * Escucha pushes reales que llegan con la app ABIERTA (en primer plano).
 * Entrega siempre la misma forma { notification: {title, body}, data }
 * sin importar si viene del plugin nativo o de Firebase Web Push.
 */
export function onForegroundPush(callback) {
  if (isNative()) {
    import("@capacitor/push-notifications").then(({ PushNotifications }) => {
      PushNotifications.addListener("pushNotificationReceived", (n) => {
        callback({
          notification: { title: n.title, body: n.body },
          data: n.data || {},
        });
      });
    });
    return;
  }
  import("./firebase").then(({ onForegroundPush: webListener }) => webListener(callback));
}
